import _, { get } from "lodash";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, DocIcon, Modal, RefreshIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { addSeconds, formatISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import DealerInfoPanel from "~/components/DealerInfoPanel";
import Preloader from "~/components/Preloader";
import ErrorMessage from "~/shared/ui/ErrorMessage/ErrorMessage";
import Checkbox from "~/shared/ui/Fields/Checkbox";
import Wrapper from "~/shared/ui/Wrapper";
import { NOTIFICATIONS } from "~/constans/notifications";
import {
  IIvideonGetterTokenSchema,
  IIvideonTokenCreateResponse,
  useIvideon,
} from "~/entity/Ivideon";
import { getCookie, setCookie } from "~/hooks/filterCookie";
import { useURLParamsGetter } from "~/hooks/useURLParamsSetter";
import {
  GetCurrentDealerContext,
  GetCurrentDealerContextPrpos,
} from "~/pages/IvideonDealerRegistry/getCurrentDealer";
import {
  useAddNotificationMutation,
  useGetDealerQuery,
  useGetProfileQuery,
} from "~/schemas/generated/graphql";
import { IvideonHTTP } from "~/services/ivideon/ivideonHTTP";
import { getConfiguredAxiosInstance } from "~/services/ivideon/lib/ConfigureAxiosInstance";
import { setIvideonCredentials } from "~/services/ivideon/lib/tokenWorkers";
import { IIvideonLocalCredentials } from "~/services/ivideon/model";
import { usePermissions } from "~/shared/context/PermissionContext";
import { TableV2 } from "~/shared/ui/TableV2";

import useGetDealerGroups from "./hooks/useGetDealerGroups";
import useGetStatusOwnerIvideon from "./hooks/useGetStatusOwnerIvideon";
import useLogoutIvideon from "./hooks/useLogoutIvideon";
import useSetAcceptGroup from "./hooks/useSetAcceptGroup";
import { hasPermissionForNameWithTypeCheck } from "./lib/hasPermissionForNameWithTypeCheck";
import { LogoutIvideon } from "./LogoutIvideon";
import { getIvideonGroupColumns } from "./model/tableAdapter";

import s from "./s.less";

const Ivideon = React.memo(function Ivideon() {
  const [ivideonIsAuth, setIvideonIsAuth] = useState(null);
  const [currentDealer, setCurrentDealer] = useState(null);
  const [open, setOpen] = useState(false);
  const [isLoadingGroupSync, setIsLoadingGroupSync] = useState<boolean>(false);

  const toggleIvideonIsAuth = (bool: boolean) => setIvideonIsAuth(bool);
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const [cameraGroupList, setCameraGroupList] = useState([]);
  const { mutateAsync, isSuccess } = useLogoutIvideon();
  const history = useHistory();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [statusAuthIvideon, setStatusAuthIvideon] = useState();
  const { code, client_id, isDealerAuth } = useURLParamsGetter<{
    client_id?: string;
    code?: string;
    isDealerAuth: string;
  }>();

  const { data: dataStatusOwnerIvideon, refetch: refetchStatusOwnerIvideon } =
    useGetStatusOwnerIvideon(id);

  const { clearDealer } = useContext<GetCurrentDealerContextPrpos>(
    GetCurrentDealerContext,
  );
  const getProfile = useGetProfileQuery({});
  const ivideonSuccess = get(
    getProfile,
    "data.UserGetter.getProfile.ivideon_access",
  );
  const logout = hasPermission("ivideon.logout");

  const storedCode = sessionStorage.getItem("auth_code") || code;
  const [isDealerAuthParams, setIsDealerAuthParams] = useState(
    isDealerAuth === "true",
  );

  const {
    data: dataGroups,
    refetch,
    isLoading,
  } = useGetDealerGroups(id, ivideonIsAuth, 1);

  const {
    data: dataAccept,
    isLoading: isLoadingAccept,
    mutate: acceptGroup,
  } = useSetAcceptGroup(Number(id), (type, message) => {
    addNotificationMutation({
      variables: {
        type: type,
        content: message,
      },
    });
  });

  useEffect(() => {
    if (dataStatusOwnerIvideon) {
      setStatusAuthIvideon(
        dataStatusOwnerIvideon?.dealers[0]?.ivideonAuth ?? false,
      );
    }
  }, [dataStatusOwnerIvideon]);

  useEffect(() => {
    if (dataGroups?.length && !isLoading) {
      setCameraGroupList(() =>
        dataGroups.filter(
          (group) =>
            (hasPermission("ivideonGroups.getGroups.sale") &&
              group.name.toLowerCase() === "зона продаж") ||
            (group.name.toLowerCase() === "зона сервиса" &&
              hasPermission("ivideonGroups.getGroups.service")),
        ),
      );
    }
  }, [dataGroups, hasPermission, isLoading]);

  useEffect(() => {
    if (dataAccept?.status === "success") {
      refetch();
    }
  }, [dataAccept]);

  const [getTokenInfo, setGetTokenInfo] = useState<
    IIvideonGetterTokenSchema | undefined
  >();
  const [getTokenInfoLoading, setGetTokenInfoLoading] =
    useState<boolean>(false);
  const { ivideonCreateToken, ivideonGetToken, ivideonSynchGroupDealer } =
    useIvideon();

  const dealerQuery = useGetDealerQuery({
    variables: {
      DealerId: id,
    },
    fetchPolicy: "no-cache",
  });
  const oneDealer = useMemo(() => {
    return get(dealerQuery, "data.DealerGetter.getDealer", {}) as Record<
      string,
      string | undefined
    >;
  }, [dealerQuery]);

  const [addNotificationMutation] = useAddNotificationMutation({});
  const cookieDealer = useIvideonDealerById(id);

  const checkTokenIsCreate = (res: IIvideonTokenCreateResponse) => {
    if (res.ok === true) {
      refetch();
    }
    if (!!res && res.ok === false) {
      document.cookie =
        "ivideon_credentials" +
        "=; expires=Thu, 01 Jan 1970 00:00:01 UTC; path=/;";
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content:
            "Дилерский центр с такими учётными данными уже зарегистрирован!",
        },
      });
      setIsDealerAuthParams(false);
      sessionStorage.setItem("IsIvideonAuthorized", "false");
      history.push(`/ivideon/dealers/${id}?isDealerAuth=false`);
      setCurrentDealer(null);
    }
  };

  useEffect(() => {
    if (dataGroups && dataGroups.length === 0 && !isLoading && getTokenInfo) {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: t("ivideonCameras.naming_zone"),
        },
      });
    }
  }, [dataGroups, isLoading, getTokenInfo]);

  useEffect(() => {
    if (dataGroups?.status === "error" && !isLoading) {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: dataGroups.message,
        },
      });
      setIsDealerAuthParams(false);
      sessionStorage.setItem("IsIvideonAuthorized", "false");
      history.push(`/ivideon/dealers/${id}?isDealerAuth=false`);
    }
  }, [dataGroups, isLoading, id]);

  useEffect(() => {
    if (cookieDealer) {
      setCurrentDealer(cookieDealer);
      setIvideonIsAuth(cookieDealer?.access_token);
    } else if (getTokenInfo) {
      setIvideonCredentials(getTokenInfo);
      setCurrentDealer(getTokenInfo);
      setIvideonIsAuth(getTokenInfo?.access_token);
    }
  }, [getTokenInfo, id]);

  const [isLoadingGroupSyncMessage, setIsLoadingGroupSyncMessage] =
    useState<string>("");

  useEffect(() => {
    if (statusAuthIvideon) {
      setGetTokenInfoLoading(true);
      ivideonGetToken(id)
        .then((res) => setGetTokenInfo(res.data.token))
        .finally(() => setGetTokenInfoLoading(false));
    }
    if (
      dataStatusOwnerIvideon &&
      statusAuthIvideon === false &&
      !isDealerAuthParams &&
      currentDealer &&
      currentDealer?.refresh_token &&
      storedCode
    ) {
      const expirationTime = formatISO(
        addSeconds(new Date(), currentDealer?.expires || 0),
      );
      setIsLoadingGroupSyncMessage("");
      setIsLoadingGroupSync(true);
      ivideonCreateToken({
        dealerId: currentDealer?.DealerId,
        access_token: currentDealer?.access_token,
        expires_in: new Date(expirationTime).toISOString(),
        refresh_token: currentDealer?.refresh_token,
        api_host: currentDealer?.api_host,
        open_api_host: currentDealer?.open_api_host,
      })
        .then((res) => {
          if (res.data.ok) {
            checkTokenIsCreate(res.data);
          } else setIsLoadingGroupSyncMessage(res.data.message);

          refetchStatusOwnerIvideon();
        })
        .finally(() => {
          setIsLoadingGroupSync(false);
        });
    }
  }, [isDealerAuthParams, dataStatusOwnerIvideon, statusAuthIvideon]);

  const getAccess = async () => {
    const ivideonFor = IvideonHTTP.getInstance(
      getConfiguredAxiosInstance({
        baseURL: `https://openapi-alpha-eu01.ivideon.com`,
        access_token: currentDealer?.access_token || "",
        refresh_token: currentDealer?.refresh_token || "",
        api_host: currentDealer?.open_api_host || "",
        api4_host: currentDealer?.api4_host || "",
        expires_in: currentDealer?.expires || 84600,
        toggleIvideonIsAuth: toggleIvideonIsAuth,
        id: id,
      }),
      toggleIvideonIsAuth,
      id,
    );

    ivideonFor
      .getAccess({
        code,
        client_id,
        id,
      })
      .finally((res) => {
        window.location.search = "";
        // setLoadingAuth(false);
      });
  };

  useEffect(() => {
    if (code && client_id) {
      getAccess();
    }
  }, [code, client_id]);

  const simpleClearCookies = () => {
    const ivideonCredentials = getCookie("ivideon_credentials");
    if (ivideonCredentials) {
      try {
        const parsedCredentials = JSON.parse(ivideonCredentials);
        if (
          parsedCredentials &&
          parsedCredentials[0] &&
          parsedCredentials[0].refresh_token
        ) {
          delete parsedCredentials[0].refresh_token;
          setCookie(
            "ivideon_credentials",
            JSON.stringify(parsedCredentials),
            120,
          );
        }
      } catch (error) {
        console.error("Ошибка при парсинге JSON:", error);
        setCookie("ivideon_credentials", JSON.stringify([]), 120);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(simpleClearCookies, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (code) {
      sessionStorage.setItem("auth_code", code);
    }
    if (!sessionStorage.getItem("filterFlag") && oneDealer?.DealerName) {
      sessionStorage.setItem("currentDealerId", id);
      sessionStorage.setItem("currentDealerName", oneDealer.DealerName);
      sessionStorage.setItem(
        "currentDealerNameEn",
        oneDealer.DealerNameEn ?? "",
      );
      sessionStorage.setItem("IsIvideonAuthorized", String(statusAuthIvideon));
    }
  }, [id, code, oneDealer, dataAccept]);

  const clientId = "autovaz_a2s";
  const redirectURI = `${window.location.origin}/ivideon/dealers/${id}`;
  const iviUrl = `https://api.ivideon.com/auth/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectURI}`;

  const handleCheckboxClick = (groupId: string, name: string) => {
    const hasAccess = hasPermissionForNameWithTypeCheck(name, hasPermission);

    // Если это сервис и нет доступа или продажа и нет доступа, то говорим что нет прав на действие
    // Если же придет null у константы type, то скип и вызываем acceptGroup
    if (!hasAccess) {
      console.error("У вас недостаточно прав для выполнения этого действия.");
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: "У вас недостаточно прав для принятия группы.",
        },
      });
      return;
    }

    acceptGroup(groupId);
  };

  const ivideonGroupColumns = getIvideonGroupColumns(
    t,
    dataGroups,
    handleCheckboxClick,
    s.checkbox,
    hasPermission,
  );

  // if (getTokenInfoLoading || !dataStatusOwnerIvideon) {
  //   return <Preloader />;
  // }

  const goToCameraList = ({ id: cameraId }) => {
    history.push(`/ivideon/dealers/${id}/${cameraId}/camera-list/`);
  };
  const goToDealerRegistry = () => {
    clearDealer();
    history.push(`/ivideon/dealers/`);
  };
  const goToRegisterOfDocuments = () => {
    history.push("/ivideon/register-of-documents");
  };

  const synchCamerasDealer = () => {
    ivideonSynchGroupDealer(id).then((res) => {
      if (res.statusText === "OK") {
        refetch();
      }
    });
  };

  const cleanAuthIvideon = () => {
    mutateAsync(id)
      .then((res) => {
        handleOpenModal();
        setStatusAuthIvideon(undefined);
      })
      .catch((error) => {
        console.error("logoutIvideon error:", error);
      });
  };

  const handleOpenModal = () => {
    setOpen((open) => !open);
  };

  return !statusAuthIvideon ? (
    <div className={s.authContainer}>
      {isLoadingGroupSync || !dataStatusOwnerIvideon ? (
        <Preloader />
      ) : (
        <>
          <Button onClick={() => (window.location.href = iviUrl)}>
            {t("ivideonCameras.auth")}
          </Button>
          {isLoadingGroupSyncMessage && (
            <ErrorMessage message={isLoadingGroupSyncMessage} />
          )}
        </>
      )}
    </div>
  ) : (
    <Wrapper.Content
      title={t("ivideonCameras.titleGroupsList")}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("ivideon.sync") && (
            <Button
              onClick={synchCamerasDealer}
              size={"middle"}
              color={"success"}
              icon={<RefreshIcon size={16} />}
            >
              Синхр. камер
            </Button>
          )}
          {hasPermission("ivideon.getDealerDocs") && (
            <Button
              onClick={goToRegisterOfDocuments}
              size={"middle"}
              color={"secondary"}
              icon={<DocIcon size={16} />}
            >
              База документов
            </Button>
          )}
          <Button
            onClick={goToDealerRegistry}
            size={"middle"}
            color={"secondary"}
            icon={<DocIcon size={16} />}
          >
            {t("ivideonCameras.dealerRegistry")}
          </Button>
        </Flex>
      }
    >
      <div className={s.container}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <DealerInfoPanel />
          <LogoutIvideon
            deleteCookie={handleOpenModal}
            currentDealer={currentDealer}
            getTokenInfo={getTokenInfo}
            logout={logout}
          />
        </div>

        <TableV2
          columns={ivideonGroupColumns}
          dataSource={cameraGroupList}
          rowKey="id"
          scroll={{ x: "max-content" }}
          loading={
            isLoading ||
            getTokenInfoLoading ||
            !dataStatusOwnerIvideon ||
            isLoadingAccept
          }
          onRow={(record) => ({
            onClick: () => goToCameraList(record),
          })}
        />
      </div>

      <Modal
        title={t("ui.confirm_action")}
        open={open}
        onCancel={handleOpenModal}
        cancelButton={{ children: "НЕТ" }}
        okButton={{ children: "ДА", onClick: cleanAuthIvideon }}
      >
        <p>Вы действительно хотите сбросить авторизацию IVIDEON?</p>
      </Modal>
    </Wrapper.Content>
  );
});

export default Ivideon;

export const useIvideonDealerById = (
  id: string,
): IIvideonLocalCredentials | null => {
  const ivideonCredentials = getCookie("ivideon_credentials");

  if (!ivideonCredentials) return null;

  const credentials: IIvideonLocalCredentials[] =
    JSON.parse(ivideonCredentials);

  const foundCredential = credentials.find((dealer) => dealer.DealerId === id);
  return foundCredential || null;
};
